import React from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

const ReactECharts = dynamic(() => import('echarts-for-react'), {
  ssr: false,
});

import Tooltip from 'components/Tooltip';

import styles from './DashboardDataBreakPointGraph.module.scss';

interface itemAttribute {
  name: string;
  value: number;
  percentage: string;
}
interface GraphResultObject {
  name: string;
  value: number;
  details?: itemAttribute[];
}
interface DashboardGraphProps {
  title?: string;
  memberData: GraphResultObject[];
  total: number;
  membersWithDetails: number;
  showFilledPercentage?: boolean;
}

interface LegendItem {
  name: string;
  value: string;
  percent: string;
}

const DashboardDataBreakPointChart: React.FC<DashboardGraphProps> = (
  props: DashboardGraphProps
) => {
  const [t] = useTranslation('community');
  const {
    title,
    memberData,
    total,
    membersWithDetails,
    showFilledPercentage = true,
  } = props;
  const getTitleLabel = (title: string): string => {
    return t(`demographic-graphs.labels.${title}`);
  };
  const readableTitle = getTitleLabel(title || '');
  const MIN_FILLED_RATIO = 0.15;

  const fillRateRatio = membersWithDetails / total;

  const options = {
    color: ['#009491', '#0C65E4', '#E5132E', '#FFCC00', '#FFFFFF'],
    title: {
      text: readableTitle,
      subtext: '',
      left: '-6px',
      top: '-3.5px',
      textStyle: {
        color: 'rgba(8, 12, 11, 0.62)',
        fontSize: '14px',
        fontWeight: '500',
        fontFamily: 'Inter',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: function (legendName: LegendItem) {
        const itemData = memberData?.find(
          (item) => item.name === legendName.name
        );
        if (legendName.name != 'Others') {
          return `${legendName.name}: ${legendName.percent}%(${
            itemData ? itemData.value : 0
          })`;
        } else {
          return itemData?.details
            ?.map(
              (item: itemAttribute) =>
                `${item.name} ${item.percentage} (${item.value})`
            )
            .join('<br />');
        }
      },
      backgroundColor: 'rgba(8, 12, 11, 0.8)',
      borderRadius: '6px',
      textStyle: {
        color: 'rgba(255, 255, 255, 1)',
      },
      position: 'right',
    },
    legend: {
      orient: 'vertical',
      left: '46%',
      top: memberData.length <= 3 ? '50' : '16',
      icon: 'circle',
      data: memberData.map((item) => item.name),
      textStyle: {
        color: '#4E4B66',
        fontSize: '11px',
      },
      formatter: function (name: string) {
        const itemData = memberData.find((item) => item.name === name);
        const truncatedName =
          name.length > 12 ? name.substring(0, 12) + '...' : name;

        return `${truncatedName} (${itemData?.value || ''})`;
      },
      tooltip: {
        show: true,
        formatter: function (legendName: LegendItem) {
          const itemData = memberData.find(
            (item) => item.name === legendName.name
          );
          if (legendName.name != 'Others') {
            return `${legendName.name}: ${itemData ? itemData.value : 0}`;
          } else {
            return itemData?.details
              ?.map(
                (item: itemAttribute) =>
                  `${item.name}: ${item.percentage}% (${item.value})`
              )
              .join('<br />');
          }
        },
      },
    },
    series: [
      {
        name: 'Data',
        type: 'pie',
        top: '30',
        radius: ['50%', '80%'],
        center: ['20%', '50%'],
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        data: memberData,
      },
    ],
  };

  return (
    <div className={styles.container}>
      {fillRateRatio < MIN_FILLED_RATIO && showFilledPercentage && (
        <Tooltip
          containerClassName={styles.tooltip}
          content={t('low-fill-rate-tooltip-content')}
          position="top"
          color="dark"
        >
          <p>{Math.floor(fillRateRatio * 100)}% filled</p>
        </Tooltip>
      )}
      <div className={styles.echartContainer}>
        <ReactECharts
          option={options}
          style={{
            minWidth: '200px',
            width: '100%',
            backgroundColor: 'rgba(8, 12, 11, 0.03)',
            padding: '22px',
            borderRadius: '8px',
            height: '180px',
          }}
        />
      </div>
    </div>
  );
};

export default DashboardDataBreakPointChart;
